import { generatePath } from 'react-router-dom';
import { createApi } from '@reduxjs/toolkit/query/react';

import { URL } from 'api/constants';

import { apiQuery } from '../../query';

import {
  Cart,
  ResetResponse,
  CartAddRequest,
  ResetCartRequest,
  CartRemoveRequest,
  CartUpdateRequest,
  UnlockCartsRequest,
  UpdateDeviceRequest,
} from './types';

export const tagTypes = ['Cart'];

export const cartApi = createApi({
  tagTypes,
  baseQuery: apiQuery,
  reducerPath: 'cartApi',
  endpoints: (build) => ({
    details: build.query<Cart, string>({
      providesTags: tagTypes,
      query: (id) => ({
        method: 'get',
        url: URL.CART.replace(':id', id),
      }),
    }),

    add: build.mutation<Cart, CartAddRequest>({
      invalidatesTags: tagTypes,
      query: ({ id, ...data }) => ({
        data,
        method: 'post',
        url: URL.CART_ADD.replace(':id', String(id)),
      }),
    }),

    unlockCarts: build.mutation<Cart[], UnlockCartsRequest>({
      invalidatesTags: tagTypes,
      query: ({ userId }) => ({
        method: 'post',
        url: URL.UNLOCK_CARTS.replace(':userId', String(userId)),
      }),
    }),

    reset: build.mutation<ResetResponse, ResetCartRequest>({
      invalidatesTags: tagTypes,
      query: ({ id, ...data }) => ({
        data,
        method: 'post',
        url: URL.CART_RESET.replace(':id', id),
      }),
    }),

    updateDevice: build.mutation<Cart, UpdateDeviceRequest>({
      invalidatesTags: tagTypes,
      query: ({ id, ...data }) => ({
        data,
        method: 'patch',
        url: URL.CART.replace(':id', String(id)),
      }),
    }),

    update: build.mutation<Cart, CartUpdateRequest>({
      invalidatesTags: tagTypes,
      query: ({ id, cartItemId, ...data }) => ({
        data,
        method: 'patch',
        url: generatePath(URL.CART_UPDATE, { id, cartItemId }),
      }),
    }),

    delete: build.mutation<Cart, CartRemoveRequest>({
      invalidatesTags: tagTypes,
      query: ({ id, cartId }) => ({
        method: 'delete',
        url: URL.CART_REMOVE_ITEM.replace(':id', String(id)).replace(
          ':cartId',
          String(cartId)
        ),
      }),
    }),
  }),
});

export type { Cart };
export const {
  useAddMutation,
  useDetailsQuery,
  useResetMutation,
  useDeleteMutation,
  useUpdateMutation,
  useUnlockCartsMutation,
  useUpdateDeviceMutation,
} = cartApi;
